import isValid from 'date-fns/isValid'
import {parseISO} from '../../../common/utils/dateUtils'

/**
 * Box opening-hours logic, kept consistent with how the mobile app evaluates
 * openStartTime / openEndTime.
 *
 * Only the wall-clock time-of-day matters; the stored date portion is ignored.
 * All evaluation happens in Europe/Stockholm (the only timezone Piffl operates
 * in for now). These helpers are independent of the admin's browser timezone:
 * they read/write the Stockholm wall-clock explicitly via Intl.
 *
 * Three valid window cases:
 *   start <  end  -> same-day window (08:00–18:00)
 *   start >  end  -> overnight window across midnight (21:00–11:00)
 *   start == end  -> open all day (24h)
 */

const STOCKHOLM_TZ = 'Europe/Stockholm'

export type OpeningWindowKind = 'same-day' | 'overnight' | 'all-day' | 'invalid'

/** Parse an "HH:mm" string to minutes-of-day, or null if malformed/out of range. */
export const toMinutes = (hhmm: string): number | null => {
  if (!/^\d{2}:\d{2}$/.test(hhmm)) return null
  const h = parseInt(hhmm.slice(0, 2), 10)
  const m = parseInt(hhmm.slice(3, 5), 10)
  if (h > 23 || m > 59) return null
  return h * 60 + m
}

const pad = (n: number): string => String(n).padStart(2, '0')

const formatStockholm = new Intl.DateTimeFormat('en-GB', {
  timeZone: STOCKHOLM_TZ,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

const extractHHmm = (date: Date): string => {
  const parts = formatStockholm.formatToParts(date)
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '00'
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00'
  return `${hour}:${minute}`
}

export const classifyOpeningWindow = (start: string, end: string): OpeningWindowKind => {
  const s = toMinutes(start)
  const e = toMinutes(end)
  if (s === null || e === null) return 'invalid'
  if (s === e) return 'all-day'
  return s < e ? 'same-day' : 'overnight'
}

/**
 * Is the box open at `nowMinutes` (minutes-of-day)?
 * Returns null when either time is malformed.
 */
export const isOpenNow = (start: string, end: string, nowMinutes: number): boolean | null => {
  const s = toMinutes(start)
  const e = toMinutes(end)
  if (s === null || e === null) return null
  if (s === e) return true // all day
  if (s < e) return nowMinutes >= s && nowMinutes < e // same-day
  return nowMinutes >= s || nowMinutes < e // overnight, wraps midnight
}

/** Current minutes-of-day in Europe/Stockholm, regardless of browser timezone. */
export const getStockholmNowMinutes = (now: Date = new Date()): number => {
  const m = toMinutes(extractHHmm(now))
  return m ?? 0
}

/** Stored ISO string -> "HH:mm" extracted in Europe/Stockholm. Empty if missing/invalid. */
export const isoToHHmmStockholm = (iso: string | undefined | null): string => {
  if (!iso) return ''
  const date = parseISO(iso)
  if (!isValid(date)) return ''
  return extractHHmm(date)
}

/**
 * "HH:mm" (Stockholm wall-clock) -> fixed-date ISO with an explicit +01:00 offset.
 * Feb 1 is always CET (+01:00, no DST) in Stockholm, so the app re-extracts the
 * same wall-clock regardless of the admin's browser timezone.
 */
export const hhmmToIsoStockholm = (hhmm: string): string | undefined => {
  const mins = toMinutes(hhmm)
  if (mins === null) return undefined
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `2000-02-01T${pad(h)}:${pad(m)}:00+01:00`
}

/** "HH:mm" -> a Date on a fixed day (local time) for use with a time picker; null if invalid. */
export const hhmmToDate = (hhmm: string): Date | null => {
  const mins = toMinutes(hhmm)
  if (mins === null) return null
  return new Date(2000, 1, 1, Math.floor(mins / 60), mins % 60)
}

/** A time picker's Date value -> "HH:mm" (local wall-clock); empty string if null/invalid. */
export const dateToHHmm = (date: Date | null): string => {
  if (!date || isNaN(date.getTime())) return ''
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Human-readable description of an opening window for display. */
export const describeOpeningWindow = (start: string, end: string): string => {
  const kind = classifyOpeningWindow(start, end)
  switch (kind) {
    case 'all-day':
      return 'Open 24h (all day)'
    case 'same-day':
      return `${start}–${end}`
    case 'overnight':
      return `${start}–${end} (overnight — across midnight)`
    case 'invalid':
      return 'Invalid time'
  }
}
